import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mountain, TreePine, Briefcase, Route } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';

import rocodomoImage from '@/assets/services/rocodromo.webp';
import aventuraImage from '@/assets/services/parque-aventura.webp';
import consultoriaImage from '@/assets/services/consultoria.webp';
import ferrataImage from '@/assets/services/via-ferrata.webp';

const ServicesSection: React.FC = () => {
  const { t } = useLanguage();

  const services = [
    {
      icon: Mountain,
      image: rocodomoImage,
      title: t('services.climbing.title'),
      description: t('services.climbing.description'),
      link: '/servicios',
    },
    {
      icon: TreePine,
      image: aventuraImage,
      title: t('services.adventure.title'),
      description: t('services.adventure.description'),
      link: '/servicios',
    },
    {
      icon: Briefcase,
      image: consultoriaImage,
      title: t('services.consulting.title'),
      description: t('services.consulting.description'),
      link: '/servicios',
    },
    {
      icon: Route,
      image: ferrataImage,
      title: t('services.ferrata.title'),
      description: t('services.ferrata.description'),
      link: '/servicios',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t('services.title')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.5, delay: index * 0.12, ease: "easeOut" }}
            >
              <Link to={service.link} className="block h-full">
                <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-border overflow-hidden h-full cursor-pointer">
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img 
                      src={service.image} 
                      alt={`${service.title} - Rock-Line España`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <service.icon className="h-5 w-5 text-accent-foreground" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">
                        {service.title}
                      </h3>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.4, delay: 0.3, ease: "easeOut" }}
        >
          <Button asChild variant="outline" size="lg">
            <Link to="/servicios">
              {t('hero.cta.services')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
